import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import "./EnterpriseIntegrations.css";
import Testimonial from "../../../Testimonial/Testimonial";

const EnterpriseIntegrations = () => {
  const techItemsRow1 = [
    { name: "Angular.js", logo: "/logos/angular.png" },
    { name: "HTML", logo: "/logos/html.png" },
    { name: "CSS3", logo: "/logos/css.png" },
    { name: "Bootstrap", logo: "/logos/bootstrap.png" },
    { name: "Javascript", logo: "/logos/javascript.png" },
    { name: "AmazonDB", logo: "/logos/amazon_dynamodb.png" },
    { name: "Appian", logo: "/logos/appian.png" },
    { name: "Laravel", logo: "/logos/laravel.png" },
    { name: "MariaDB", logo: "/logos/mariadb.png" },
    { name: "MongoDB", logo: "/logos/mongodb.png" },
    { name: "Android", logo: "/logos/android-5.png" },
    { name: "ERC-20", logo: "/logos/erc-20.png" },
    { name: "Ethereum", logo: "/logos/ethereum.png" },
    { name: "Flutter", logo: "/logos/flutter.png" },
    { name: "Ionic", logo: "/logos/ionic.png" },
    { name: "Augmented Reality", logo: "/logos/Augmented Reality.png" },
    { name: "Crda", logo: "/logos/crda.png" },
    { name: "Data Science", logo: "/logos/data-science.png" },
    { name: "Go Chain", logo: "/logos/gochain.png" },
  ];

  const techItemsRow2 = [
    { name: "Sass", logo: "/logos/saas.png" },
    { name: "JQuery", logo: "/logos/jquery.png" },
    { name: "Less", logo: "/logos/less.png" },
    { name: "Codeigniter", logo: "/logos/codeignitor.png" },
    { name: "Django", logo: "/logos/django.png" },
    { name: "Node JS", logo: "/logos/node js.png" },
    { name: "PHP", logo: "/logos/php.png" },
    { name: "Salesforce", logo: "/logos/salesforce.png" },
    { name: "SQL Server", logo: "/logos/sql-server.png" },
    { name: "MySQL", logo: "/logos/mysql-5.png" },
    { name: "IOS", logo: "/logos/ios.png" },
    { name: "Oracle 6", logo: "/logos/oracle.png" },
    { name: "PostgreSQL", logo: "/logos/postgresql.png" },
    { name: "React Native", logo: "/logos/react_native.png" },
    { name: "Solidity", logo: "/logos/Solidity.png" },
    { name: "Hyperledger", logo: "/logos/Hyperledger.png" },
    { name: "Internet Of Things", logo: "/logos/iot.png" },
    { name: "One Ledger", logo: "/logos/OneLedger.png" },
    { name: "Virtual Reality", logo: "/logos/virtual-reality.png" },
  ];

  const duplicatedRow1 = [...techItemsRow1, ...techItemsRow1];
  const duplicatedRow2 = [...techItemsRow2, ...techItemsRow2];

  const containerRef = useRef(null);
  const row1Ref = useRef(null);
  const row2Ref = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [offset, setOffset] = useState(0);
  const animationRef = useRef();

  useEffect(() => {
    // Set initial container width
    if (containerRef.current) {
      setContainerWidth(containerRef.current.offsetWidth);
    }

    const handleResize = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const scrollSpeed = 1; // Adjust speed as needed (pixels per frame)

    const animate = () => {
      setOffset((prevOffset) => {
        const maxOffset = row1Ref.current?.scrollWidth / 2 || 0;
        const newOffset = prevOffset + scrollSpeed;
        return newOffset >= maxOffset ? 0 : newOffset;
      });
      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(animationRef.current);
    };
  }, [containerWidth]);

  return (
    <div>
      <Helmet>
        <title>
          Enterprise Integration Solutions | Seamless System Connectivity
        </title>
        <meta
          name="description"
          content="Comprehensive enterprise integration services connecting your business systems with APIs, middleware, and cloud solutions for seamless data flow and operational efficiency."
        />
        <meta
          name="keywords"
          content="enterprise integrations, system integration, API integration, middleware solutions, cloud integration, data integration, business process integration, ETL tools, iPaaS, ESB, enterprise service bus, application integration, database integration, CRM integration, ERP integration, SaaS integration, legacy system integration, real-time integration, workflow automation, data synchronization, MuleSoft, Dell Boomi, Informatica, IBM Integration Bus, Oracle SOA, SAP PI, Azure Integration Services, AWS integration, hybrid cloud integration, microservices integration, event-driven architecture, API management, data transformation, business-to-business integration, EDI solutions, system connectivity, digital transformation, enterprise architecture, integration patterns, data governance, integration platform, cloud-native integration, hybrid integration, enterprise application integration, business intelligence integration, IoT integration, AI integration, blockchain integration"
        />
      </Helmet>

      <div className="mk-enterprise-integration">
        {/* Hero Section */}
        <section className="mk-ei-hero">
          <div className="mk-ei-hero-content">
            <h1 className="mk-ei-hero-title">
              Enterprise Integration Solutions
            </h1>
            <p className="mk-ei-hero-subtitle">
              Seamlessly connect your business systems for enhanced productivity
              and data flow
            </p>
            <a href="/contact-us"><button className="mk-ei-cta-button">Get Started</button></a>
          </div>
          <div className="mk-ei-hero-animation">
            <div className="mk-ei-circle mk-ei-circle-1"></div>
            <div className="mk-ei-circle mk-ei-circle-2"></div>
            <div className="mk-ei-circle mk-ei-circle-3"></div>
            <div className="mk-ei-connection-line mk-ei-line-1"></div>
            <div className="mk-ei-connection-line mk-ei-line-2"></div>
            <div className="mk-ei-connection-line mk-ei-line-3"></div>
          </div>
        </section>

        {/* Services Section */}
        <section className="mk-ei-services">
          <h2 className="mk-ei-section-title">Our Integration Services</h2>
          <div className="mk-ei-services-grid">
            <div className="mk-ei-service-card">
              <div className="mk-ei-service-icon">
                <i className="mk-ei-icon-api"></i>
              </div>
              <h3 className="mk-ei-service-title">API Integration</h3>
              <p className="mk-ei-service-desc">
                Connect your applications with robust API solutions for
                real-time data exchange.
              </p>
            </div>

            <div className="mk-ei-service-card">
              <div className="mk-ei-service-icon">
                <i className="mk-ei-icon-data"></i>
              </div>
              <h3 className="mk-ei-service-title">Data Synchronization</h3>
              <p className="mk-ei-service-desc">
                Keep your data consistent across all platforms with our
                synchronization solutions.
              </p>
            </div>

            <div className="mk-ei-service-card">
              <div className="mk-ei-service-icon">
                <i className="mk-ei-icon-cloud"></i>
              </div>
              <h3 className="mk-ei-service-title">Cloud Integration</h3>
              <p className="mk-ei-service-desc">
                Bridge the gap between on-premise and cloud systems for a hybrid
                environment.
              </p>
            </div>

            <div className="mk-ei-service-card">
              <div className="mk-ei-service-icon">
                <i className="mk-ei-icon-workflow"></i>
              </div>
              <h3 className="mk-ei-service-title">Workflow Automation</h3>
              <p className="mk-ei-service-desc">
                Automate business processes across different systems to improve
                efficiency.
              </p>
            </div>

            {/* New Services Added Below */}
            <div className="mk-ei-service-card">
              <div className="mk-ei-service-icon">
                <i className="mk-ei-icon-legacy"></i>
              </div>
              <h3 className="mk-ei-service-title">
                Legacy System Modernization
              </h3>
              <p className="mk-ei-service-desc">
                Integrate your legacy systems with modern applications to extend
                their lifespan and functionality.
              </p>
            </div>

            <div className="mk-ei-service-card">
              <div className="mk-ei-service-icon">
                <i className="mk-ei-icon-b2b"></i>
              </div>
              <h3 className="mk-ei-service-title">B2B Integration</h3>
              <p className="mk-ei-service-desc">
                Streamline business processes with partners, suppliers, and
                customers through secure electronic data exchange.
              </p>
            </div>

            <div className="mk-ei-service-card">
              <div className="mk-ei-service-icon">
                <i className="mk-ei-icon-iot"></i>
              </div>
              <h3 className="mk-ei-service-title">IoT Integration</h3>
              <p className="mk-ei-service-desc">
                Connect and manage IoT devices, sensors, and data streams within
                your enterprise ecosystem.
              </p>
            </div>

            <div className="mk-ei-service-card">
              <div className="mk-ei-service-icon">
                <i className="mk-ei-icon-analytics"></i>
              </div>
              <h3 className="mk-ei-service-title">
                Business Intelligence Integration
              </h3>
              <p className="mk-ei-service-desc">
                Unify data from multiple sources to create comprehensive
                dashboards and analytics platforms.
              </p>
            </div>

            {/* Additional Services */}
            <div className="mk-ei-service-card">
              <div className="mk-ei-service-icon">
                <i className="mk-ei-icon-erp"></i>
              </div>
              <h3 className="mk-ei-service-title">ERP Integration</h3>
              <p className="mk-ei-service-desc">
                Seamlessly connect your ERP system with other business
                applications for unified operations.
              </p>
            </div>

            <div className="mk-ei-service-card">
              <div className="mk-ei-service-icon">
                <i className="mk-ei-icon-mobile"></i>
              </div>
              <h3 className="mk-ei-service-title">Mobile App Integration</h3>
              <p className="mk-ei-service-desc">
                Integrate mobile applications with backend systems for seamless
                data access and functionality.
              </p>
            </div>

            <div className="mk-ei-service-card">
              <div className="mk-ei-service-icon">
                <i className="mk-ei-icon-security"></i>
              </div>
              <h3 className="mk-ei-service-title">Security Integration</h3>
              <p className="mk-ei-service-desc">
                Implement robust security measures and identity management
                across all integrated systems.
              </p>
            </div>

            <div className="mk-ei-service-card">
              <div className="mk-ei-service-icon">
                <i className="mk-ei-icon-ecommerce"></i>
              </div>
              <h3 className="mk-ei-service-title">E-commerce Integration</h3>
              <p className="mk-ei-service-desc">
                Connect your e-commerce platforms with inventory, CRM, and
                payment systems for streamlined operations.
              </p>
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="mk-ei-benefits">
  <h2 className="mk-ei-section-title">Benefits of Enterprise Integration</h2>
  <div className="mk-ei-benefits-container">
    <div className="mk-ei-benefit-item">
      <div className="mk-ei-benefit-number">01</div>
      <h3 className="mk-ei-benefit-title">Increased Efficiency</h3>
      <p className="mk-ei-benefit-desc">
        Eliminate manual data entry and reduce errors with automated workflows.
      </p>
    </div>

    <div className="mk-ei-benefit-item">
      <div className="mk-ei-benefit-number">02</div>
      <h3 className="mk-ei-benefit-title">Enhanced Visibility</h3>
      <p className="mk-ei-benefit-desc">
        Gain a unified view of your business operations across all systems.
      </p>
    </div>

    <div className="mk-ei-benefit-item">
      <div className="mk-ei-benefit-number">03</div>
      <h3 className="mk-ei-benefit-title">Scalability</h3>
      <p className="mk-ei-benefit-desc">
        Easily adapt your integration solutions as your business grows.
      </p>
    </div>

    <div className="mk-ei-benefit-item">
      <div className="mk-ei-benefit-number">04</div>
      <h3 className="mk-ei-benefit-title">Cost Reduction</h3>
      <p className="mk-ei-benefit-desc">
        Lower operational costs by streamlining processes and reducing manual work.
      </p>
    </div>
    
    {/* New Benefits Added Below */}
    <div className="mk-ei-benefit-item">
      <div className="mk-ei-benefit-number">05</div>
      <h3 className="mk-ei-benefit-title">Improved Decision Making</h3>
      <p className="mk-ei-benefit-desc">
        Access real-time data and analytics for more informed business decisions.
      </p>
    </div>

    <div className="mk-ei-benefit-item">
      <div className="mk-ei-benefit-number">06</div>
      <h3 className="mk-ei-benefit-title">Enhanced Customer Experience</h3>
      <p className="mk-ei-benefit-desc">
        Provide seamless customer interactions across all touchpoints with unified data.
      </p>
    </div>

    <div className="mk-ei-benefit-item">
      <div className="mk-ei-benefit-number">07</div>
      <h3 className="mk-ei-benefit-title">Increased Agility</h3>
      <p className="mk-ei-benefit-desc">
        Respond faster to market changes with flexible and adaptable integrated systems.
      </p>
    </div>

    <div className="mk-ei-benefit-item">
      <div className="mk-ei-benefit-number">08</div>
      <h3 className="mk-ei-benefit-title">Better Compliance</h3>
      <p className="mk-ei-benefit-desc">
        Maintain regulatory compliance with consistent data governance across all systems.
      </p>
    </div>

    {/* Additional Benefits */}
    <div className="mk-ei-benefit-item">
      <div className="mk-ei-benefit-number">09</div>
      <h3 className="mk-ei-benefit-title">Data Accuracy & Consistency</h3>
      <p className="mk-ei-benefit-desc">
        Ensure data integrity across all systems with automated validation and synchronization.
      </p>
    </div>

    <div className="mk-ei-benefit-item">
      <div className="mk-ei-benefit-number">10</div>
      <h3 className="mk-ei-benefit-title">Faster Time-to-Market</h3>
      <p className="mk-ei-benefit-desc">
        Accelerate product launches and service deployments with pre-integrated systems.
      </p>
    </div>

    <div className="mk-ei-benefit-item">
      <div className="mk-ei-benefit-number">11</div>
      <h3 className="mk-ei-benefit-title">Competitive Advantage</h3>
      <p className="mk-ei-benefit-desc">
        Gain an edge over competitors with streamlined operations and innovative capabilities.
      </p>
    </div>

    <div className="mk-ei-benefit-item">
      <div className="mk-ei-benefit-number">12</div>
      <h3 className="mk-ei-benefit-title">Business Continuity</h3>
      <p className="mk-ei-benefit-desc">
        Ensure operational resilience with integrated backup and disaster recovery solutions.
      </p>
    </div>
  </div>
</section>

        {/* Case Studies Section */}
<section className="mk-ei-cases">
  <h2 className="mk-ei-section-title">Success Stories</h2>
  <p className="mk-ei-section-subtitle">
    Discover how our enterprise integration solutions have transformed businesses across industries
  </p>
  
  <div className="mk-ei-cases-container">
    <div className="mk-ei-case-study">
      <div className="mk-ei-case-badge">Retail Industry</div>
      <div className="mk-ei-case-content">
        <h3 className="mk-ei-case-title">Global Retail Chain Integration</h3>
        <div className="mk-ei-case-stats">
          <div className="mk-ei-stat">
            <span className="mk-ei-stat-number">15+</span>
            <span className="mk-ei-stat-label">Systems Integrated</span>
          </div>
          <div className="mk-ei-stat">
            <span className="mk-ei-stat-number">200+</span>
            <span className="mk-ei-stat-label">Locations</span>
          </div>
          <div className="mk-ei-stat">
            <span className="mk-ei-stat-number">40%</span>
            <span className="mk-ei-stat-label">Faster Reporting</span>
          </div>
          <div className="mk-ei-stat">
            <span className="mk-ei-stat-number">30%</span>
            <span className="mk-ei-stat-label">Error Reduction</span>
          </div>
        </div>
        <p className="mk-ei-case-desc">
          We implemented a comprehensive integration solution for a global retail chain with operations across 12 countries. 
          The challenge was to connect disparate systems including inventory management, POS, e-commerce, CRM, and supply chain 
          applications. Our solution enabled real-time data synchronization across all touchpoints, providing a unified view of 
          inventory, customer data, and sales performance. The results included streamlined operations, improved customer experience, 
          and significant cost savings through optimized inventory management.
        </p>
        <div className="mk-ei-case-footer">
          {/* <a href="#" className="mk-ei-case-link">
            Read Full Case Study <span className="mk-ei-case-arrow">→</span>
          </a>
          <span className="mk-ei-case-duration">Completed in 6 months</span> */}
        </div>
      </div>
    </div>

    <div className="mk-ei-case-study">
      <div className="mk-ei-case-badge">Healthcare</div>
      <div className="mk-ei-case-content">
        <h3 className="mk-ei-case-title">Healthcare System Interoperability</h3>
        <div className="mk-ei-case-stats">
          <div className="mk-ei-stat">
            <span className="mk-ei-stat-number">20+</span>
            <span className="mk-ei-stat-label">Hospitals Connected</span>
          </div>
          <div className="mk-ei-stat">
            <span className="mk-ei-stat-number">50%</span>
            <span className="mk-ei-stat-label">Faster Data Access</span>
          </div>
          <div className="mk-ei-stat">
            <span className="mk-ei-stat-number">99.9%</span>
            <span className="mk-ei-stat-label">Uptime</span>
          </div>
          <div className="mk-ei-stat">
            <span className="mk-ei-stat-number">$2.5M</span>
            <span className="mk-ei-stat-label">Annual Savings</span>
          </div>
        </div>
        <p className="mk-ei-case-desc">
          A major healthcare provider needed to connect disparate electronic health record (EHR) systems, laboratory information 
          systems, and patient management applications across multiple facilities. Our team designed and implemented a secure, 
          HIPAA-compliant integration platform that enabled seamless data exchange while maintaining strict privacy controls. 
          The solution eliminated duplicate data entry, reduced medical errors through better information access, and improved 
          patient outcomes with comprehensive health records available to authorized providers across the network.
        </p>
        {/* <div className="mk-ei-case-footer">
          <a href="#" className="mk-ei-case-link">
            Read Full Case Study <span className="mk-ei-case-arrow">→</span>
          </a>
          <span className="mk-ei-case-duration">Completed in 9 months</span>
        </div> */}
      </div>
    </div>

    <div className="mk-ei-case-study">
      <div className="mk-ei-case-badge">Manufacturing</div>
      <div className="mk-ei-case-content">
        <h3 className="mk-ei-case-title">Supply Chain Integration Solution</h3>
        <div className="mk-ei-case-stats">
          <div className="mk-ei-stat">
            <span className="mk-ei-stat-number">45%</span>
            <span className="mk-ei-stat-label">Faster Order Processing</span>
          </div>
          <div className="mk-ei-stat">
            <span className="mk-ei-stat-number">60%</span>
            <span className="mk-ei-stat-label">Inventory Reduction</span>
          </div>
          <div className="mk-ei-stat">
            <span className="mk-ei-stat-number">25%</span>
            <span className="mk-ei-stat-label">Cost Savings</span>
          </div>
          <div className="mk-ei-stat">
            <span className="mk-ei-stat-number">100+</span>
            <span className="mk-ei-stat-label">Suppliers Connected</span>
          </div>
        </div>
        <p className="mk-ei-case-desc">
          For a global manufacturing company, we created an end-to-end supply chain integration solution that connected ERP, 
          inventory management, supplier portals, and logistics systems. The implementation enabled real-time visibility into 
          inventory levels, production status, and shipment tracking across the entire supply chain. Automated workflows 
          streamlined procurement processes, reduced stockouts, and optimized inventory levels. The solution also provided 
          predictive analytics for demand forecasting, allowing for more accurate production planning and reduced waste.
        </p>
        {/* <div className="mk-ei-case-footer">
          <a href="#" className="mk-ei-case-link">
            Read Full Case Study <span className="mk-ei-case-arrow">→</span>
          </a>
          <span className="mk-ei-case-duration">Completed in 8 months</span>
        </div> */}
      </div>
    </div>
  </div>

  <div className="mk-ei-cases-cta">
    <p>Want to see similar results for your business?</p>
    <a
  href="https://wa.me/919155599968?text=Hi!%20I%20would%20like%20to%20discuss%20Enterprise%20Integration%20services%20such%20as%20system%20integration%2C%20API%20management%2C%20and%20workflow%20automation.%20Please%20connect%20me%20with%20your%20experts."
  target="_blank"
  rel="noreferrer"
>
  <button className="mk-ei-cta-button">Talk to Our Experts</button>
</a>

  </div>
</section>

        {/* CTA Section */}
        <section className="mk-ei-cta">
          <div className="mk-ei-cta-container">
            <h2 className="mk-ei-cta-title">
              Ready to Transform Your Business Integration?
            </h2>
            <p className="mk-ei-cta-text">
              Contact our experts today for a free consultation and integration
              assessment.
            </p>
            <a
              href="https://wa.me/919155599968?text=Hi!%20I%20am%20interested%20in%20Enterprise%20Integration%20solutions%20such%20as%20system%20integration%2C%20API%20management%2C%20and%20workflow%20automation.%20Please%20schedule%20a%20consultation%20with%20your%20experts."
              target="_blank"
              rel="noreferrer"
            >
              <button className="mk-ei-cta-button mk-ei-cta-button-large">
                Schedule a Consultation
              </button>
            </a>
          </div>
        </section>
      </div>

      <Testimonial />

      <div className="tech-slider-container" ref={containerRef}>
        <h2 className="tech-slider-title">Tools & Tech Stack</h2>
        <div className="tech-slider-divider"></div>

        {/* First row */}
        <div className="tech-slider-wrapper">
          <div
            className="tech-slider-row"
            ref={row1Ref}
            style={{ transform: `translateX(-${offset}px)` }}
          >
            {duplicatedRow1.map((tech, index) => (
              <div key={`row1-${index}`} className="tech-slide">
                <img
                  src={tech.logo}
                  alt={tech.name}
                  className="tech-logo"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "/logos/default.png";
                  }}
                />
                <span className="tech-name">{tech.name}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Second row */}
        <div className="tech-slider-wrapper">
          <div
            className="tech-slider-row"
            ref={row2Ref}
            style={{ transform: `translateX(-${offset}px)` }}
          >
            {duplicatedRow2.map((tech, index) => (
              <div key={`row2-${index}`} className="tech-slide">
                <img
                  src={tech.logo}
                  alt={tech.name}
                  className="tech-logo"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "/logos/default.png";
                  }}
                />
                <span className="tech-name">{tech.name}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnterpriseIntegrations;
